import { useEffect, useState } from 'react';

import type { Tier } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';

const getAllPriceIds = (tiers: Tier[]) => tiers.flatMap((tier) => tier.prices?.map((p) => p.id) ?? []);

const getFirstValidPriceId = (
  tiers: Tier[],
  tiersOrder: UpgradePage['main_section']['tiers_order'],
): string | undefined => {
  // Try sorted tiers order first (skip 'free' tier)
  const firstSortedTierId = tiersOrder?.[0]?.id;
  if (firstSortedTierId) {
    const tierIdToUse = firstSortedTierId === 'free' ? tiersOrder[1]?.id : firstSortedTierId;
    const priceFromOrder = tiers.find((tier) => tier.id === tierIdToUse)?.prices[0]?.id;
    if (priceFromOrder) return priceFromOrder;
  }

  // Fall back to first tier's first price
  return tiers[0]?.prices[0]?.id;
};

const useSelectedPriceId = (mostPopularPriceId: string, tiers: Tier[], upgradePage: UpgradePage) => {
  const allPriceIds = getAllPriceIds(tiers);
  const validMostPopular = allPriceIds.includes(mostPopularPriceId) ? mostPopularPriceId : undefined;
  const initialPriceId = validMostPopular || tiers[0]?.prices[0]?.id || '';

  const [priceId, setPriceId] = useState<string>(initialPriceId);

  const mainSection = upgradePage?.main_section;

  useEffect(() => {
    const currentPriceIds = getAllPriceIds(tiers);
    if (currentPriceIds.includes(priceId)) return; // Current selection is valid

    // Find a new valid price
    const validPopular = currentPriceIds.includes(mostPopularPriceId) ? mostPopularPriceId : undefined;
    const newPriceId = validPopular || getFirstValidPriceId(tiers, mainSection?.tiers_order);
    if (newPriceId) setPriceId(newPriceId);
  }, [tiers, mostPopularPriceId, priceId, mainSection]);

  return { priceId, setPriceId };
};

export default useSelectedPriceId;
